/**
 * OS-keychain ``AndoStorage`` adapter — backed by the Rust ``keyring``
 * crate through three Tauri commands (``secret_get`` / ``secret_set`` /
 * ``secret_delete``).
 *
 * Where secrets actually live, per platform:
 *   - macOS   → login Keychain (``Keychain Access.app``)
 *   - Windows → Credential Manager (``Generic Credential``)
 *   - Linux   → Secret Service (GNOME Keyring / KWallet via D-Bus)
 *
 * Every entry is stored under service ``ai.ando.desktop`` with account
 * ``secret/<key>``. The value is the JSON encoding of whatever the caller
 * passed to ``set`` so the ``get<T>`` contract matches ``WebStorage``.
 *
 * Security model: the OS owns encryption-at-rest and access control.
 * There is NO derived vault password, NO snapshot file and NO KDF on our
 * side — the credential store is unlocked with the user's OS session. On
 * macOS the first access after a (re)install shows the system dialog
 * "ando-desktop wants to use your confidential information stored in
 * ... in your keychain" — clicking *Always Allow* is a one-time step per
 * signed binary; a rebuilt/unsigned binary prompts again.
 *
 * Key constraints (enforced by the Rust side): 1-200 chars, no ``/``, no
 * ``..``, no control characters.
 *
 * ``clear()`` limitation: the ``keyring`` crate exposes no cheap
 * "enumerate every account under a service" API from JS, so ``clear()``
 * only deletes the keys this instance has SEEN during the current process
 * (every key passed to ``get`` / ``set`` / ``remove``). Secrets written by
 * a previous launch and never touched in this one are NOT removed. Callers
 * that need a guaranteed wipe must ``remove()`` each well-known key
 * explicitly.
 */

import { invoke } from "@tauri-apps/api/core";
import type { AndoStorage } from "./types.js";

export class KeyringStorage implements AndoStorage {
  /** Keys touched in this process — the only ones ``clear()`` can reach. */
  private readonly seen = new Set<string>();
  private warnedCorrupt = false;

  /**
   * No-op: the OS keychain needs no unlock step from us. Kept so call sites
   * written against the previous vault adapter keep compiling.
   */
  async init(): Promise<void> {
    /* intentionally empty */
  }

  async get<T>(key: string): Promise<T | null> {
    this.seen.add(key);
    const raw = await invoke<string | null>("secret_get", { key });
    if (raw === null || raw === undefined) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      if (!this.warnedCorrupt) {
        this.warnedCorrupt = true;
        console.warn(
          `[KeyringStorage] corrupt (non-JSON) value under "${key}" — treating as absent`,
        );
      }
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    this.seen.add(key);
    await invoke("secret_set", { key, value: JSON.stringify(value) });
  }

  async remove(key: string): Promise<void> {
    this.seen.add(key);
    await invoke("secret_delete", { key });
  }

  /**
   * Deletes every key this instance has seen in the current process.
   * See the module docblock — this is NOT a full wipe of the keychain
   * service; untouched entries from earlier launches survive.
   */
  async clear(): Promise<void> {
    const keys = [...this.seen];
    this.seen.clear();
    await Promise.all(keys.map((key) => invoke("secret_delete", { key })));
  }
}
